import { Box, Button, Flex, HStack, Heading, Image, Spinner, Text, useBoolean } from '@chakra-ui/react';
import bgBrandModelImage from 'assets/background/bg_auto_brand.png';
import { BRAND_REDIRECT_LINK } from 'constants/links';
import { updateBrand, uploadImage } from 'libs/e1api/autoBrand';
import {
  AUTO_BRAND_ADDED,
  AUTO_BRAND_FONTS,
  AUTO_BRAND_SKIPPED,
  Mixpanel,
  NEW_BRAND_ADDED,
} from 'libs/third-party/Mixpanel';
import { RootState, store, useAppDispatch } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { setBrandIdx } from 'modules/Editor/store/editorSettingsSlice';
import { BrandState, useBrand } from 'modules/User';
import { useToast } from 'modules/common/components/Toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseFiniteNumberWithNaN } from 'utils/number';

import { DEFAULT_BRAND_FONTS } from './AutoBrand.config';
import { AutoBrandEntity, createMixpanelAutoFontEvent, getAutoBrand, getBrandLogos } from './AutoBrand.helpers';
import { AutoBrandFormProps } from './AutoBrand.types';
import { AutoCompleteBrandSearch } from './AutoCompleteBrandSearch';
import { SubmitValue } from './AutoCompleteBrandSearch.types';
import Brands from './Brands';

export const AutoBrandForm = ({
  handleTabsChange = () => {},
  closeModal,
  toggleAutoBrand,
  hasBrand,
  createNew,
  onClickSetupManually,
}: AutoBrandFormProps) => {
  const { t } = useTranslation('auto_brand', { useSuspense: false, keyPrefix: 'modals.autoBrand' });

  const [errorMsg, setError] = useState('');
  const [isLoading, setLoading] = useBoolean(false);
  const [brandURL, setBrandURL] = useState('');
  const [brandData, setBrandData] = useState<AutoBrandEntity>();
  const toast = useToast();
  const userBrand = useBrand();
  const dispatch = useAppDispatch();

  const onSetBrandIdx = (value: string) => {
    const idx = parseFiniteNumberWithNaN(value);
    dispatch(setBrandIdx(idx));
  };

  const handleUpdateBrand = async () => {
    if (brandData?.logos && userBrand?.data && userBrand?.data?.length > 0) {
      const brandIdx = selectBrandIdx(store.getState() as RootState);
      let id: string;
      let brandProps;
      if (createNew) {
        const newBrand = await updateBrand({ name: 'Brand Kit', metadata: { site_url: brandURL } });
        id = newBrand.id;
        brandProps = { ...newBrand.brand };
        Mixpanel.track(NEW_BRAND_ADDED);
      } else {
        const currentBrand = userBrand.data[brandIdx] || userBrand?.data[0];
        id = currentBrand.id || '';
        brandProps = { ...currentBrand };
      }

      const uploadedLogos = await Promise.all(
        brandData?.logos.map((logoBlob: Blob) => {
          return uploadImage(logoBlob, id);
        }),
      );

      // Parse store state into saveable format
      const saveBrandData: BrandState = {
        id,
        ...brandProps,
        colors: [brandData.colors],
        logos: uploadedLogos.map((logo) => logo.data.mediaFile),
      };

      let titleFontFamily, subtitleFontFamily, textFontFamily;
      if (brandData.fonts) {
        titleFontFamily = brandData.fonts.find((f) => f.type === 'title' && f.isVenngageFont)?.fontFamily;
        subtitleFontFamily = brandData.fonts.find((f) => f.type === 'subtitle' && f.isVenngageFont)?.fontFamily;
        textFontFamily = brandData.fonts.find((f) => f.type === 'text' && f.isVenngageFont)?.fontFamily;
      }

      if (titleFontFamily) {
        saveBrandData.title = {
          ...(brandProps.title ? brandProps.title : DEFAULT_BRAND_FONTS.title),
          fontFamily: titleFontFamily,
        };
      }

      if (subtitleFontFamily) {
        saveBrandData.subtitle = {
          ...(brandProps.subtitle ? brandProps.subtitle : DEFAULT_BRAND_FONTS.subtitle),
          fontFamily: subtitleFontFamily,
        };
      }

      if (textFontFamily) {
        saveBrandData.text = {
          ...(brandProps.text ? brandProps.text : DEFAULT_BRAND_FONTS.text),
          fontFamily: textFontFamily,
        };
      }

      try {
        const metadataLogos = await getBrandLogos({ logos: uploadedLogos.map((l) => l.data.mediaFile) });
        if (metadataLogos) {
          saveBrandData.metadata = {
            ...saveBrandData.metadata,
            logos: metadataLogos,
          };
        }
      } catch (e) {
        console.error(e);
      }

      return await updateBrand(saveBrandData);
    } else {
      console.info('user has no default brand data');
    }
  };

  const handleConfirmAction = async (e: React.FormEvent) => {
    e?.preventDefault();

    if (hasBrand) {
      try {
        setLoading.on();
        const results = await handleUpdateBrand();
        setLoading.off();
        if (results?.status === 'fail') {
          toast({ title: results?.message || t('failureMessage'), status: 'error' });
        } else if (results?.status === 'ok') {
          Mixpanel.track(AUTO_BRAND_ADDED, { site: brandURL });
          // this is used to change the tab from the menu when a brand kit is added
          handleTabsChange();
          userBrand.refresh();
          if (createNew) {
            // select the new brand
            onSetBrandIdx(String(userBrand?.data?.length || 0));
          }
          closeModal();
          toast({ title: t('brandKitSetup'), status: 'success' });
        }
      } catch (error) {
        console.error(error);
        toast({ title: t('failureMessage'), status: 'error' });
      }
    }
  };

  const fetchAutoBrand = async (newBrandURL: string) => {
    if (isLoading || !newBrandURL) return;
    setBrandURL(newBrandURL);

    try {
      setLoading.on();

      const brandResults = await getAutoBrand(newBrandURL);
      setLoading.off();

      if (brandResults.colors.length === 0 || brandResults.logos.length === 0) {
        setError(t('unable'));
      } else {
        toggleAutoBrand.on();
        setBrandData(brandResults);

        if (brandResults.fonts && brandResults.fonts.length > 0) {
          const mixpanelAutoFontEvent = createMixpanelAutoFontEvent(brandResults.fonts);
          Mixpanel.track(AUTO_BRAND_FONTS, mixpanelAutoFontEvent);
        }
      }
    } catch (error) {
      setLoading.off();
      setError(t('unable'));
    }
  };

  /**
   * redirect the user to the brand and handles MP
   */
  const redirectUserToBrands = () => {
    Mixpanel.track(AUTO_BRAND_SKIPPED);
    if (onClickSetupManually) {
      onClickSetupManually();
      return;
    }
    window.open(BRAND_REDIRECT_LINK, '_blank');
  };

  const onSubmitBrandAutoComplete = (brand: SubmitValue) => fetchAutoBrand(brand.value);
  const onClearError = () => {
    if (errorMsg) {
      setError('');
    }
  };

  return (
    <Flex flexDirection={'row'}>
      <Flex flex='1' flexDirection={'column'} borderStartRadius={6} paddingX={6} paddingY={8}>
        <HStack>
          {isLoading && <Spinner color='violet.600' data-testid={'spinner-auto-brand-domain'} />}
          <Heading fontSize={24} role='alert' aria-live='polite'>
            {hasBrand ? t('isThatYourBrand') : t('setupYour')}
          </Heading>
        </HStack>
        <Text marginY={5} fontSize='sm'>
          {t('simplyEnter')}
        </Text>

        <AutoCompleteBrandSearch
          onSubmit={onSubmitBrandAutoComplete}
          placeholder={t('inputPlaceholder')}
          onClear={onClearError}
          onChange={onClearError}
          initialValue={brandURL}
          errorMessage={errorMsg}
          isDisabled={isLoading}
        />
        <HStack marginTop={'auto'} alignSelf={'flex-end'}>
          <Button
            variant={'ghost'}
            isDisabled={isLoading}
            onClick={redirectUserToBrands}
            data-testid={'setup-brand-button'}
          >
            {hasBrand ? t('noSetupManually') : t('setupManually')}
          </Button>

          <Button
            variant='purple'
            size={'sm'}
            paddingX={6}
            disabled={isLoading || !hasBrand}
            onClick={handleConfirmAction}
            data-testid={'next-confirm-brand-button'}
          >
            {t('confirm')}
          </Button>
        </HStack>
      </Flex>

      <Box flex='1' bg='#F9F9F9' borderLeft='1px solid' borderColor='gray.100' borderEndRadius={6}>
        {hasBrand ? (
          <Brands brands={brandData} />
        ) : (
          <Image src={bgBrandModelImage} minH='430px' minW='336px' role='presentation' alt='' borderEndRadius={6} />
        )}
      </Box>
    </Flex>
  );
};
